import React, { useContext, useEffect, useState } from "react";
import { useUser } from "../utils/AccountStore";
import { Attachment, GetQuestionnaire, GetQuestionnaireScopes, ListScopeSequences, QuestionnaireScopeAPIModel, ScopeTypeSequenceAPIModel, UploadAttachment } from "../utils/ServiceManagerAPI";
import SkeletonWrapper from "../components/SkeltonWrapper";
import { filter, set } from "lodash";
import RenderFile from "../components/RenderFile";
import ScrollContainer from "../components/ScrollContainer";
import StyledModal from "../components/StyledModel";
import { useNavigate } from "react-router-dom";
import { useEntities } from "../components/EntitiesContext";
import "../main.css";

const Disposal = () => {
    const { user, setUser } = useUser();
    const { getEntityById } = useEntities();
    const [loading, setLoading] = useState(true);

    const [sequences, setSequences] = useState([] as ScopeTypeSequenceAPIModel[]);
    const [usedScopes, setUsedScopes] = useState([] as QuestionnaireScopeAPIModel[]);
    const [filteredUsedScopes, setFilteredUsedScopes] = useState([] as QuestionnaireScopeAPIModel[]);

    const [selectedScopes, setSelectedScopes] = useState([] as QuestionnaireScopeAPIModel[]);
    const [entityNamesByQuestionnaireID, setEntityNamesByQuestionnaireID] = useState<Record<string, string>>({});
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 25;

    const [typeFilter, setTypeFilter] = useState([] as string[]);
    const [filter, setFilter] = useState<string>("");

    const [error, setError] = useState<string | null>(null);

    const [isComplete, setIsComplete] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (user) {
            Promise.all([
                ListScopeSequences().then((res) => {
                    setSequences(res.value);
                    setTypeFilter(res.value.map((sequence) => sequence.id));
                }),
                GetQuestionnaireScopes(WasteFormID).then((res) => {
                    setUsedScopes(res);
                }),
            ])
                .then(() => {
                    setLoading(false);
                })
                .catch((err) => {
                    setError("Loading scopes failed: " + err.message);
                });
        } else {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        setFilteredUsedScopes(
            usedScopes.filter((scope) => {
                return typeFilter.includes(scope.scopeTypeSequenceID) && scope.formattedSequenceNumber.toLowerCase().includes(filter.toLowerCase());
            }),
        );
    }, [usedScopes, typeFilter, filter]);

    useEffect(() => {
        setCurrentPage(1);
    }, [typeFilter, filter]);

    const totalPages = Math.max(1, Math.ceil(filteredUsedScopes.length / pageSize));
    const pageStart = (currentPage - 1) * pageSize;
    const pagedUsedScopes = filteredUsedScopes.slice(pageStart, pageStart + pageSize);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    useEffect(() => {
        const questionnaireIDs = Array.from(new Set(pagedUsedScopes.map((scope) => scope.questionnaireID).filter(Boolean))).filter((questionnaireID) => !entityNamesByQuestionnaireID[questionnaireID]);

        if (!questionnaireIDs.length) {
            return;
        }

        let isDisposed = false;

        Promise.all(
            questionnaireIDs.map(async (questionnaireID) => {
                try {
                    const res = await GetQuestionnaire(questionnaireID);
                    const entityID = res?.questionnaire?.subjectID;
                    const entityName = entityID ? getEntityById(entityID)?.name || "Unknown Entity" : "Unknown Entity";
                    return [questionnaireID, entityName] as const;
                } catch {
                    return [questionnaireID, "Unknown Entity"] as const;
                }
            }),
        ).then((entries) => {
            if (!isDisposed) {
                setEntityNamesByQuestionnaireID((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
            }
        });

        return () => {
            isDisposed = true;
        };
    }, [pagedUsedScopes, entityNamesByQuestionnaireID, getEntityById]);

    const toggleTypeFilter = (type: string) => {
        if (typeFilter.includes(type)) {
            setTypeFilter(typeFilter.filter((t) => t != type));
        } else {
            setTypeFilter([...typeFilter, type]);
        }
    };

    const toggleScope = (checked: boolean, scope: QuestionnaireScopeAPIModel) => {
        if (checked) {
            setSelectedScopes([...selectedScopes, scope]);
        } else {
            setSelectedScopes(selectedScopes.filter((s) => s.questionnaireID != scope.questionnaireID));
        }
    };

    const [step, setStep] = useState(0);

    const [files, setFiles] = useState([] as File[]);
    const handleContentChange = (event: React.ChangeEvent<HTMLInputElement> | undefined) => {
        const files = event?.target.files;
        setFiles(files ? Array.from(files) : []);
    };

    const complete = async () => {
        setLoading(true);
        try {
            const uploadPromises = files.flatMap((content) =>
                selectedScopes.map((scope) => {
                    return new Promise<void>((resolve, reject) => {
                        if (scope && content) {
                            const reader = new FileReader();
                            reader.readAsDataURL(content);
                            reader.onload = (evt: any) => {
                                UploadAttachment({
                                    content: evt.target.result.replace(/^data:(\w+|\-|\/|\.)+;base64,/, ""),
                                    contentType: content.type,
                                    name: content.name,
                                    questionID: DisposalCertificateQuestionID,
                                    questionnaireID: scope.questionnaireID,
                                } as Attachment)
                                    .then(() => resolve())
                                    .catch((err) => reject(err));
                            };
                            reader.onerror = (err) => reject(err);
                        } else {
                            resolve();
                        }
                    });
                }),
            );

            await Promise.all(uploadPromises).catch((err) => {
                setError("Uploading files failed: " + err.message);
            });
            setIsComplete(true);
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : String(error);
            setError("Uploading files failed: " + message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="">
            <SkeletonWrapper loading={loading}>
                <>
                    <div className="flex items-center justify-between py-4 px-2">
                        <div
                            className={`px-3 py-1.5 rounded border-2 shrink-0 ${step >= 0 ? "bg-brand text-white border-brand" : "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"}`}
                        >
                            <span className="text-sm">Step 1: Choose Manifests</span>
                        </div>
                        <div className={`flex-1 h-0.5 mx-3 transition-colors ${step >= 1 ? "bg-brand" : "bg-gray-300 dark:bg-gray-600"}`} />
                        <div
                            className={`px-3 py-1.5 rounded border-2 shrink-0 ${step >= 1 ? "bg-brand text-white border-brand" : "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"}`}
                        >
                            <span className="text-sm">Step 2: Upload Certificate</span>
                        </div>
                        <div className={`flex-1 h-0.5 mx-3 transition-colors ${step >= 2 ? "bg-brand" : "bg-gray-300 dark:bg-gray-600"}`} />
                        <div
                            className={`px-3 py-1.5 rounded border-2 shrink-0 ${step >= 2 ? "bg-brand text-white border-brand" : "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"}`}
                        >
                            <span className="text-sm">Step 3: Confirm</span>
                        </div>
                    </div>

                    {step == 0 && (
                        <div className="p-4 flex flex-col gap-2">
                            <div className="flex flex-row gap-2 items-center flex-wrap">
                                <span className="text-gray-900 dark:text-gray-100">Filter:</span>
                                {sequences.map((sequence) => (
                                    <button
                                        className={`px-3 py-1 rounded text-sm transition-colors ${typeFilter.includes(sequence.id) ? "bg-brand text-white" : "bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600"}`}
                                        key={sequence.id}
                                        onClick={() => {
                                            toggleTypeFilter(sequence.id);
                                        }}
                                    >
                                        {sequence.name}
                                    </button>
                                ))}
                            </div>

                            <input
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                placeholder="Filter by sequence number (prefix)"
                                className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 w-full text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 text-sm"
                            />
                            <span className="text-gray-900 dark:text-gray-100">Manifests:</span>
                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr className="border-b border-gray-300 dark:border-gray-600">
                                            <th className="text-left p-2 text-gray-900 dark:text-gray-100">Entity</th>
                                            <th className="text-left p-2 text-gray-900 dark:text-gray-100">Sequence Number</th>
                                            <th className="text-left p-2 text-gray-900 dark:text-gray-100">Group</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pagedUsedScopes.map((scope) => (
                                            <tr
                                                key={scope.id}
                                                className={`border-b border-gray-200 dark:border-gray-700 cursor-pointer ${selectedScopes.some((s) => s.id == scope.id) ? "bg-brand/10" : "hover:bg-gray-100 dark:hover:bg-gray-800"}`}
                                                onClick={() => toggleScope(!selectedScopes.some((s) => s.id == scope.id), scope)}
                                            >
                                                <td className="p-2">
                                                    <span className="text-gray-500 dark:text-gray-400">{entityNamesByQuestionnaireID[scope.questionnaireID] || "Loading..."}</span>
                                                </td>
                                                <td className="p-2">
                                                    <span className="text-brand cursor-pointer">
                                                        {scope.scopeTypeSequence?.name} {scope.formattedSequenceNumber}
                                                    </span>
                                                </td>
                                                <td className="p-2">
                                                    <span className="text-gray-900 dark:text-gray-100">{scope.scopeGroup.name}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="flex items-center justify-between mt-2">
                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                    Showing {filteredUsedScopes.length === 0 ? 0 : pageStart + 1}-{Math.min(pageStart + pageSize, filteredUsedScopes.length)} of {filteredUsedScopes.length}
                                </span>
                                <div className="flex flex-row gap-2">
                                    <button
                                        className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50"
                                        onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                                        disabled={currentPage === 1}
                                    >
                                        Previous
                                    </button>
                                    <span className="px-2 py-1 text-sm text-gray-900 dark:text-gray-100">
                                        Page {currentPage} / {totalPages}
                                    </span>
                                    <button
                                        className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50"
                                        onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                                        disabled={currentPage === totalPages}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                            <div className="flex flex-row justify-end gap-2">
                                <button
                                    className="px-4 py-2 rounded bg-brand text-white hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={() => setStep(1)}
                                    disabled={selectedScopes.length == 0}
                                >
                                    Next
                                </button>
                            </div>
                            {selectedScopes.length == 0 && <span className="text-red-500">Select at least one manifest before continuing</span>}
                        </div>
                    )}

                    {step == 1 && (
                        <div className="p-4 flex flex-col gap-2">
                            <div className="flex flex-col gap-1">
                                <label className="text-sm text-gray-700 dark:text-gray-300">Upload Certificate</label>
                                <input
                                    type="file"
                                    multiple
                                    onChange={handleContentChange}
                                    className="block w-full text-sm text-gray-900 dark:text-gray-100 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-brand file:text-white hover:file:bg-brand-dark"
                                />
                            </div>
                            <div className="flex flex-row justify-between gap-2">
                                <button
                                    className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                                    onClick={() => setStep(0)}
                                >
                                    Back
                                </button>
                                <button className="px-4 py-2 rounded bg-brand text-white hover:bg-brand-dark transition-colors" onClick={() => setStep(2)}>
                                    Next
                                </button>
                            </div>
                            {files.map((file) => (
                                <RenderFile file={file} key={file.name}></RenderFile>
                            ))}
                            {files.length > 0 && (
                                <div className="flex flex-row justify-between gap-2">
                                    <button
                                        className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                                        onClick={() => setStep(0)}
                                    >
                                        Back
                                    </button>
                                    <button className="px-4 py-2 rounded bg-brand text-white hover:bg-brand-dark transition-colors" onClick={() => setStep(2)}>
                                        Next
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    {step == 2 && (
                        <div className="p-4 flex flex-col gap-2">
                            <span className="text-gray-900 dark:text-gray-100">Files:</span>
                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr className="border-b border-gray-300 dark:border-gray-600">
                                            <th className="text-left p-2 text-gray-900 dark:text-gray-100">Name</th>
                                            <th className="text-left p-2 text-gray-900 dark:text-gray-100">Type</th>
                                            <th className="text-left p-2 text-gray-900 dark:text-gray-100">Size</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {files.map((file) => (
                                            <tr key={file.name} className="border-b border-gray-200 dark:border-gray-700">
                                                <td className="p-2 text-gray-900 dark:text-gray-100">{file.name}</td>
                                                <td className="p-2 text-gray-900 dark:text-gray-100">{file.type}</td>
                                                <td className="p-2 text-gray-900 dark:text-gray-100">{file.size}</td>
                                            </tr>
                                        ))}
                                        {files.length === 0 && (
                                            <tr>
                                                <td className="p-2 text-gray-500 dark:text-gray-400" colSpan={3}>
                                                    No files selected, please go back and choose files to upload.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <span className="text-gray-900 dark:text-gray-100">Upload to:</span>
                            <div className="grid grid-cols-[1fr_1fr_3fr] gap-2 items-center">
                                {selectedScopes.map((scope) => {
                                    return (
                                        <React.Fragment key={scope.id}>
                                            <td className="p-2">
                                                <span className="text-gray-500 dark:text-gray-400">{entityNamesByQuestionnaireID[scope.questionnaireID] || "Loading..."}</span>
                                            </td>
                                            <td className="p-2">
                                                <span className="text-brand">
                                                    {scope.scopeTypeSequence?.name} {scope.formattedSequenceNumber}
                                                </span>
                                            </td>
                                            <td className="p-2">
                                                <span className="text-gray-900 dark:text-gray-100">{scope.scopeGroup.name}</span>
                                            </td>
                                        </React.Fragment>
                                    );
                                })}
                            </div>

                            <div className="flex flex-row justify-between gap-2">
                                <div>
                                    <button
                                        className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                                        onClick={() => setStep(1)}
                                    >
                                        Back
                                    </button>
                                </div>
                                <div>
                                    <button className="px-4 py-2 rounded bg-brand text-white hover:bg-brand-dark transition-colors disabled:opacity-50" disabled={files.length === 0} onClick={() => complete()}>
                                        Complete
                                    </button>
                                    {files.length === 0 && (
                                        <tr>
                                            <td className="p-2 text-red-500 dark:text-red-400" colSpan={3}>
                                                No files selected, please go back and choose files to upload.
                                            </td>
                                        </tr>
                                    )}
                                </div>
                            </div>
                            <StyledModal isOpen={isComplete}>
                                <span className="text-gray-900 dark:text-gray-100">Disposal certificate has been successfully uploaded to the selected manifests.</span>
                                <div className="flex flex-row justify-end gap-2 mt-4">
                                    <button className="px-4 py-2 rounded bg-brand text-white hover:bg-brand-dark transition-colors" onClick={() => navigate("/")}>
                                        OK
                                    </button>
                                </div>
                            </StyledModal>
                        </div>
                    )}
                </>
            </SkeletonWrapper>
            {error && (
                <StyledModal isOpen={!!error}>
                    <div className="p-2 flex flex-col gap-2">
                        <span className="text-red-500 font-semibold">An error occurred:</span>
                        <span className="text-gray-900 dark:text-gray-100">{error}</span>
                        <div className="flex flex-row justify-end gap-2">
                            <button
                                className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:cursor-not-allowed"
                                onClick={() => setError(null)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </StyledModal>
            )}
        </div>
    );
};

export default Disposal;
